import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { translateFarmsError } from '../lib/translateError';
import type { FarmOrder, FarmOrderEvent, OrderStatus, PaymentMethod } from '../types';

export function useFarmOrder() {
  const { tenantId } = useTenant();
  const { user }     = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function createOrder(params: {
    farm_id:               string;
    specs:                 Record<string, unknown>;
    supplier_amount_cents: number;
    platform_fee_cents:    number;
    total_amount_cents:    number;
    initialStatus?:        'quote' | 'reserved';
  }): Promise<FarmOrder> {
    if (!tenantId || !user) throw new Error('Autenticação necessária.');
    // Atestado de leigo é criado sem preço (o dono monta o orçamento depois).
    const isAtestado = (params.specs as { atestado_leigo?: boolean })?.atestado_leigo === true;
    if (params.total_amount_cents < 0) throw new Error('Valor de pedido inválido.');
    if (!isAtestado && params.total_amount_cents <= 0) throw new Error('Valor de pedido inválido.');
    setLoading(true);
    setError('');
    try {
      const { initialStatus = 'quote', ...orderParams } = params;
      const { data, error: err } = await supabase
        .from('farm_orders')
        .insert({
          customer_tenant_id:    tenantId,
          customer_user_id:      user.id,
          ...orderParams,
          status:                initialStatus,
          payment_status:        'pending',
        } as never)
        .select('*')
        .single();
      if (err) throw new Error(translateFarmsError(err.message));
      return data as unknown as FarmOrder;
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(orderId: string, status: OrderStatus): Promise<void> {
    const { error: err } = await supabase
      .from('farm_orders')
      .update({ status } as never)
      .eq('id', orderId);
    if (err) throw new Error(translateFarmsError(err.message));
  }

  async function simulatePayment(orderId: string, method: PaymentMethod): Promise<void> {
    const { error: err } = await supabase
      .from('farm_orders')
      .update({ payment_method: method, payment_status: 'authorized' } as never)
      .eq('id', orderId);
    if (err) throw new Error(translateFarmsError(err.message));
  }

  return { createOrder, updateStatus, simulatePayment, loading, error };
}

export interface MyOrder extends FarmOrder {
  farms: { name: string; slug: string } | null;
}

export function useMyOrders() {
  const { user }     = useAuth();
  const { tenantId } = useTenant();
  const [orders, setOrders]   = useState<MyOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('farm_orders')
        .select('*, farms(name, slug)')
        .eq('customer_tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (err) throw new Error(translateFarmsError(err.message));
      setOrders((data as unknown as MyOrder[]) ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar pedidos.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const cancelOrder = useCallback(async (orderId: string) => {
    const { error: err } = await supabase
      .from('farm_orders')
      .update({ status: 'cancelled' } as never)
      .eq('id', orderId);
    if (err) throw new Error(translateFarmsError(err.message));
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: 'cancelled' } : o)));
  }, []);

  return { orders, loading, error, fetchOrders, cancelOrder, userId: user?.id };
}

export function useFarmOrders(farmId: string) {
  const [orders, setOrders]   = useState<FarmOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const fetchOrders = async () => {
    if (!farmId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('farm_orders')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });
      if (err) throw new Error(translateFarmsError(err.message));
      setOrders((data as unknown as FarmOrder[]) ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar pedidos da farm.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return { orders, loading, error, fetchOrders };
}

// ─── Detalhe de um pedido + timeline de eventos ───────────────────────────────

export interface OrderWithFarm extends FarmOrder {
  farms: { name: string; slug: string } | null;
}

export function useOrderDetail(orderId: string) {
  const [order, setOrder]   = useState<OrderWithFarm | null>(null);
  const [events, setEvents] = useState<FarmOrderEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    Promise.all([
      supabase
        .from('farm_orders')
        .select('*, farms(name, slug)')
        .eq('id', orderId)
        .single(),
      supabase
        .from('farm_order_events')
        .select('*')
        .eq('order_id', orderId)
        .order('changed_at', { ascending: true }),
    ]).then(([orderRes, eventsRes]) => {
      if (orderRes.error) {
        setError(translateFarmsError(orderRes.error.message));
      } else {
        setOrder(orderRes.data as unknown as OrderWithFarm);
        setEvents((eventsRes.data ?? []) as FarmOrderEvent[]);
      }
      setLoading(false);
    });
  }, [orderId]);

  return { order, events, loading, error };
}

// ─── Contexto do pedido (partes envolvidas) ───────────────────────────────────

export interface OrderContext {
  farm_id:            string;
  farm_name:          string;
  farm_slug:          string;
  farm_owner_user_id: string;
  customer_user_id:   string;
  customer_label:     string | null;
  viewer_is_owner:    boolean;
}

/** RPC get_order_context (migration 20260706000002): identifica farm e
 *  solicitante do pedido, e se quem está vendo é o dono da farm. */
export function useOrderContext(orderId: string) {
  const [context, setContext] = useState<OrderContext | null>(null);
  useEffect(() => {
    if (!orderId) return;
    supabase
      .rpc('get_order_context' as never, { p_order_id: orderId } as never)
      .then(({ data }) => {
        const rows = (data as unknown as OrderContext[]) ?? [];
        setContext(rows[0] ?? null);
      });
  }, [orderId]);
  return context;
}

// ─── Caixa de entrada do dono da farm (pedidos recebidos) ─────────────────────

export interface FarmOrderWithName extends FarmOrder {
  farmName: string;
}

export function useFarmInbox() {
  const { tenantId } = useTenant();
  const [orders, setOrders]     = useState<FarmOrderWithName[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    const tid = tenantId;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: farmData } = await supabase
          .from('farms')
          .select('id, name')
          .eq('owner_tenant_id', tid);
        const farms = (farmData as { id: string; name: string }[]) ?? [];
        if (farms.length === 0) { setOrders([]); return; }

        const farmIds  = farms.map((f) => f.id);
        const farmMap  = new Map(farms.map((f) => [f.id, f.name]));

        const { data, error: err } = await supabase
          .from('farm_orders')
          .select('*')
          .in('farm_id', farmIds)
          .order('created_at', { ascending: false });
        if (err) throw new Error(translateFarmsError(err.message));

        setOrders(
          ((data as FarmOrder[]) ?? []).map((o) => ({
            ...o,
            farmName: farmMap.get(o.farm_id) ?? 'Farm',
          }))
        );
      } catch (e: any) {
        setError(e.message ?? 'Erro ao carregar pedidos recebidos.');
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [tenantId]);

  async function changeStatus(orderId: string, status: OrderStatus): Promise<void> {
    const { error: err } = await supabase
      .from('farm_orders')
      .update({ status } as never)
      .eq('id', orderId);
    if (err) throw new Error(translateFarmsError(err.message));
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
  }

  return { orders, loading, error, changeStatus };
}
